/**
 * damage_layer — PERSISTENT MATERIAL DAMAGE, the material half (shader gaps program round 2, 2026-09-29). The pure
 * store and the CPU oracles of every formula below are `damage_state.ts`; the game wiring is `damage_runtime.ts`.
 *
 * `applyDamageLayer(material, { mode })` composes the damage onto a material's OWN colour / roughness / metalness —
 * and, in 'full', its emissive (heat) and normal (the crater) — BEFORE lighting, the way `material_finish`
 * composes the grime: every slot wraps the prior node (or three's `material*` accessor), so the maps, the grime, the
 * snow and the detail surface all survive, and a surface with no mark near it evaluates to EXACTLY its old value
 * (base·1 + 0; the normal is selected, not re-normalised). Tom Looman's wound rendering is the model: hits kept in
 * the receiver's LOCAL space, sphere masks evaluated in the material, no render target.
 *
 * DATA. ONE uniform array: the records (3 vec4 each, `damage_state.RECORD_VEC4`) then the profile table (4 vec4 per
 * surface × weapon), in three's RENDER group — ONE upload per render call shared by every material. (In the
 * default object group a uniform array is a per-draw buffer that three re-uploads for EVERY render object: `Buffer.
 * update()` is always true and an OBJECT group always updates — 4 KB × every building batch × every frame.) No
 * sampler is spent (the ground's texture budget is full) and no vertex buffer / location (8 / 16 on WebGPU; an
 * instanced building batch under TAAU already spends 8 locations on the current + previous instance matrix).
 *
 * WHICH RECORDS a draw evaluates is PER OBJECT (`onObjectUpdate`): a mover (the ride hull, a struck air target)
 * reads its own range and measures the fragment in its own frame through the root's inverse world matrix — the
 * marks ride it and no re-upload follows the motion; everything else (settlement batches, deeds, props, the ground)
 * reads the STATIC set, resolved to RENDER space around the camera by the runtime. So an instanced building needs no
 * slot bookkeeping at all: its swap-remove, its LOD crossfade (two representations drawn) and its LOD0 refinement all
 * show the same marks at the same place.
 *
 * THE ORDER LAW (r185): `normalWorld` / `normalView` outside the NORMAL sub-build ARE the material's `setupNormal`
 * result — i.e. our own `normalNode`. The evaluation therefore reads the GEOMETRY normal (`normalWorldGeometry`) and
 * runs FIRST in the colour slot (three builds `colorNode` before roughness, metalness, lighting and the normal), so
 * every later slot reads assigned variables and a prior colour graph that touches `normalWorld` (the grime) cannot
 * recurse into an unevaluated damage.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, If, Loop, int, float, vec3, vec4, uniform, uniformArray, renderGroup, positionWorld, normalWorldGeometry,
  materialColor, materialRoughness, materialMetalness, materialEmissive, materialNormal, cameraViewMatrix,
  normalize, dot, length, smoothstep, saturate, mix, max, abs, pow, exp, floor, fract, sin, step, select, transpose, Stack,
} from 'three/tsl';
import {
  PROFILE_COUNT, packProfiles, SLAB_MIN_M, SLAB_R, FACING_E0, FACING_E1, EDGE_E0, TEMPER_RAMP, RING_CAP, packStatic, packMover,
  type DamageState, type Vec3,
} from './damage_state.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL node graphs are loosely typed
type N = any;

/**
 * Records the shader can see at once (static set + every mover's range). 48 × 3 vec4 + the 21 profiles × 4 = 228 vec4
 * = 3.6 KB — ONE uniform block, kept small: the v2 ground already carries the water bed's 11 KB block (a WebGL2
 * uniform block is guaranteed only 16 KB, and a stage only 12 blocks).
 */
export const DAMAGE_MAX_RECORDS = 48;
export type DamageMode = 'off' | 'marks' | 'full';

/**
 * ONE uniform array (one buffer binding: WebGPU allows 12 uniform buffers per stage and three already spends several):
 * slots [3i, 3i+1, 3i+2] are record i's A / B / C; the profile table follows at PROF_BASE, four vec4 per profile.
 */
const PROF_BASE = DAMAGE_MAX_RECORDS * 3;
const slots = Array.from({ length: PROF_BASE + PROFILE_COUNT * 4 }, () => new THREE.Vector4());
{
  const prof = packProfiles();
  for (let i = 0; i < PROFILE_COUNT * 4; i++) slots[PROF_BASE + i].set(prof[i * 4], prof[i * 4 + 1], prof[i * 4 + 2], prof[i * 4 + 3]);
}
/** The CPU side of the record arrays (the runtime / bed pack here, then `flushDamageRecords`). */
export const damageRecordData = { A: new Float32Array(DAMAGE_MAX_RECORDS * 4), B: new Float32Array(DAMAGE_MAX_RECORDS * 4), C: new Float32Array(DAMAGE_MAX_RECORDS * 4) };
const uDmg: N = (uniformArray(slots, 'vec4') as N).setGroup(renderGroup);

/** 1 = the crater normal and the heat are live (a 'full' graph); 0 turns both off without a rebuild ('marks' / VR). */
export const uDamageFull: N = uniform(1);
/** Scene radiance per unit heat (the runtime writes `emissiveNits(…)` every frame — THE PHYSICAL-EMISSIVE CONTRACT). */
export const uDamageHeat: N = uniform(0);

/** Copy the packed CPU arrays into the uniform arrays (slots [0, n)); three uploads them once per render. */
export function flushDamageRecords(n: number): void {
  const { A, B, C } = damageRecordData;
  for (let i = 0; i < Math.min(n, DAMAGE_MAX_RECORDS); i++) {
    const o = i * 4;
    slots[i * 3].set(A[o], A[o + 1], A[o + 2], A[o + 3]);
    slots[i * 3 + 1].set(B[o], B[o + 1], B[o + 2], B[o + 3]);
    slots[i * 3 + 2].set(C[o], C[o + 1], C[o + 2], C[o + 3]);
  }
}

// ─── the upload (the runtime and the bed share it) ─────────────────────────

export interface DamageUploadCache { sel: ReturnType<DamageState['selectStatic']>; version: number; at: number; cx: number; cy: number; cz: number; selections: number }
export function newUploadCache(): DamageUploadCache { return { sel: [], version: -1, at: -1e9, cx: 1e12, cy: 0, cz: 0, selections: 0 }; }
export interface DamageUploadOpts {
  /** The camera's ABSOLUTE position, the render origin (render = absolute − origin), the session clock (s). */
  cam: Vec3; origin: Vec3; now: number;
  staticMax: number; staticRange: number;
  /** The movers that may draw (the root carries `userData.damageRecv` on its meshes), within `moverRange` of the camera. */
  movers: Iterable<{ id: string; root: THREE.Object3D }>; moverRange: number;
}
const _mp = new THREE.Vector3();
/**
 * Resolve this frame's records into the uniform array: the static set nearest the camera in RENDER space (re-selected
 * when the store changed, the camera moved 12 m or a second passed; re-packed every frame — heat and the origin move),
 * then each visible mover's records in its own frame. Returns what it uploaded.
 */
export function uploadDamage(state: DamageState, cache: DamageUploadCache, o: DamageUploadOpts): { statics: number; movers: number } {
  const [cx, cy, cz] = o.cam;
  if (state.version !== cache.version || Math.hypot(cx - cache.cx, cy - cache.cy, cz - cache.cz) > 12 || o.now - cache.at > 1) {
    cache.sel = state.selectStatic(cx, cy, cz, o.staticMax, o.staticRange);
    cache.version = state.version; cache.at = o.now; cache.cx = cx; cache.cy = cy; cache.cz = cz; cache.selections++;
  }
  const { A, B, C } = damageRecordData;
  let n = packStatic(cache.sel, A, B, C, 0, o.staticMax, o.origin, o.now);
  damageStaticRange.start = 0; damageStaticRange.count = n;
  let mv = 0;
  for (const m of o.movers) {
    const b = moverBinding(m.id);
    b.root = m.root; b.count = 0; b.frameId = -1; // the frame is taken again at the next draw
    const r = state.receiver(m.id);
    if (!r || !r.records.length || !m.root.parent || n >= DAMAGE_MAX_RECORDS) continue;
    m.root.getWorldPosition(_mp);
    if (Math.hypot(_mp.x + o.origin[0] - cx, _mp.y + o.origin[1] - cy, _mp.z + o.origin[2] - cz) > o.moverRange) continue;
    b.start = n;
    b.count = packMover(r, A, B, C, n, Math.min(RING_CAP.mover, DAMAGE_MAX_RECORDS - n), o.now);
    n += b.count; mv++;
  }
  flushDamageRecords(n);
  return { statics: damageStaticRange.count, movers: mv };
}

// ─── per-object binding ────────────────────────────────────────────────────

/**
 * Which records a drawn object evaluates. A mover's frame is its ROOT's inverse world matrix, taken AT DRAW TIME
 * (the renderer has updated every matrixWorld by then — at RenderSub the hull's matrix is still last frame's, and a
 * ship at cruise would drag its marks a few metres behind); once per render frame per mover (`frameId`).
 */
export interface DamageBinding { start: number; count: number; root: THREE.Object3D | null; frame: THREE.Matrix4; frameId: number }
const NONE: DamageBinding = { start: 0, count: 0, root: null, frame: new THREE.Matrix4(), frameId: -1 };
/** The static set's range (the runtime writes it every frame); its frame is the identity (records are in render space). */
export const damageStaticRange: DamageBinding = { start: 0, count: 0, root: null, frame: new THREE.Matrix4(), frameId: -1 };
/** Mover ranges by receiver id (the runtime fills; an object carries `userData.damageRecv = id`). */
export const damageMoverRanges = new Map<string, DamageBinding>();
export function moverBinding(id: string): DamageBinding {
  let b = damageMoverRanges.get(id);
  if (!b) { b = { start: 0, count: 0, root: null, frame: new THREE.Matrix4(), frameId: -1 }; damageMoverRanges.set(id, b); }
  return b;
}
/** Tag a mover's meshes so the draw reads ITS range and frame (never the static set); `root` is its frame. */
export function markMoverMesh(id: string, root: THREE.Object3D): void {
  root.traverse((o) => { (o.userData as Record<string, unknown>).damageRecv = id; });
  moverBinding(id).root = root;
}
function bindingOf(o: THREE.Object3D | null | undefined): DamageBinding {
  const id = o?.userData?.damageRecv as string | undefined;
  if (id) return damageMoverRanges.get(id) ?? NONE; // a mover never reads the static set (it would carry the ground's marks)
  return damageStaticRange;
}
type ObjFrame = { object?: THREE.Object3D | null; frameId?: number };
const uStart: N = uniform(0).onObjectUpdate(((f: ObjFrame) => bindingOf(f.object).start) as never);
const uCount: N = uniform(0).onObjectUpdate(((f: ObjFrame) => bindingOf(f.object).count) as never);
const uFrame: N = uniform(new THREE.Matrix4()).onObjectUpdate(((f: ObjFrame, self: { value: THREE.Matrix4 }) => {
  const b = bindingOf(f.object);
  if (b.root && b.count > 0) {
    const id = f.frameId ?? 0;
    if (b.frameId !== id) { b.frame.copy(b.root.matrixWorld).invert(); b.frameId = id; }
  }
  self.value.copy(b.root && b.count > 0 ? b.frame : damageStaticRange.frame);
}) as never);

// ─── shader helpers ────────────────────────────────────────────────────────

/** A sin-fract lattice hash (sign-safe by construction — three's `hash()` saturates negative seeds, shaders.md §0). */
const hash3 = (c: N): N => fract(sin(dot(c, vec3(12.9898, 78.233, 37.719))).mul(43758.5453));
function valueNoise(p: N): N {
  const i = floor(p), f = fract(p);
  const s = f.mul(f).mul(float(3).sub(f.mul(2)));
  const c = (x: number, y: number, z: number): N => hash3(i.add(vec3(x, y, z)));
  const x00 = mix(c(0, 0, 0), c(1, 0, 0), s.x), x10 = mix(c(0, 1, 0), c(1, 1, 0), s.x);
  const x01 = mix(c(0, 0, 1), c(1, 0, 1), s.x), x11 = mix(c(0, 1, 1), c(1, 1, 1), s.x);
  return mix(mix(x00, x10, s.y), mix(x01, x11, s.y), s.z);
}
const T = TEMPER_RAMP.map((c) => vec3(c[0], c[1], c[2]));
/** Piecewise-linear over the four temper stops (== damage_state.temperColorCpu). */
const temperColor = (t: N): N => { const t3 = saturate(t).mul(3); return mix(mix(mix(T[0], T[1], saturate(t3)), T[2], saturate(t3.sub(1))), T[3], saturate(t3.sub(2))); };
/** Glowing metal / embers: deep red → orange with heat. */
const heatColor = (h: N): N => mix(vec3(1.0, 0.16, 0.03), vec3(1.0, 0.55, 0.2), saturate(h));

interface Slots { aK: N; aC: N; rK: N; rC: N; mK: N; mC: N; heat: N; grad: N; any: N }

/** THE EVALUATION (one per material graph): loops this object's records and fills the affine accumulators. */
function evaluate(S: Slots, full: boolean): void {
  const pF: N = uFrame.mul(vec4(positionWorld, 1)).xyz.toVar();
  const gF: N = normalize(uFrame.mul(vec4(normalWorldGeometry, 0)).xyz).toVar();
  const over = (w: N, ak: N, ac: N, rk: N, rc: N, mk: N, mc: N, wm: N): void => {
    const iw = float(1).sub(w), iwm = float(1).sub(wm);
    S.aK.assign(S.aK.mul(iw).add(vec3(ak).mul(w)));
    S.aC.assign(S.aC.mul(iw).add(vec3(ac).mul(w)));
    S.rK.assign(S.rK.mul(iw).add(float(rk).mul(w)));
    S.rC.assign(S.rC.mul(iw).add(float(rc).mul(w)));
    S.mK.assign(S.mK.mul(iwm).add(float(mk).mul(wm)));
    S.mC.assign(S.mC.mul(iwm).add(float(mc).mul(wm)));
  };
  Loop({ start: int(uStart), end: int(uStart.add(uCount)), type: 'int', condition: '<' }, ({ i }: { i: N }) => {
    const a: N = uDmg.element(i.mul(3));
    const R: N = max(a.w, float(1e-4));
    const d: N = pF.sub(a.xyz);
    const slab: N = max(float(SLAB_MIN_M), R.mul(SLAB_R));
    const reach: N = R.mul(1.02).add(slab);
    If(dot(d, d).lessThan(reach.mul(reach)), () => {
      const b: N = uDmg.element(i.mul(3).add(1));
      const c: N = uDmg.element(i.mul(3).add(2));
      const n: N = b.xyz;
      const dn: N = dot(d, n);
      const dt: N = d.sub(n.mul(dn));
      const dtl: N = length(dt);
      const rho: N = dtl.div(R);
      const slabW: N = float(1).sub(smoothstep(slab.mul(0.6), slab, abs(dn)));
      const facing: N = smoothstep(FACING_E0, FACING_E1, dot(gF, n));
      const pb: N = int(c.x).mul(4).add(PROF_BASE);
      const P0: N = uDmg.element(pb), P1: N = uDmg.element(pb.add(1)), P2: N = uDmg.element(pb.add(2)), P3: N = uDmg.element(pb.add(3));
      const coreFrac: N = P2.x, temperW: N = P2.w;
      // a ragged edge: two octaves of value noise on the mark's own tangent-plane offset (+ the record's seed) —
      // offset-relative, so it neither swims at a rebase nor slides on a moving hull
      const q: N = dt.div(R).mul(3).add(vec3(b.w, b.w.mul(1.7), b.w.mul(0.61)));
      const nz: N = valueNoise(q).mul(0.65).add(valueNoise(q.mul(2.3)).mul(0.35));
      const rn: N = rho.mul(float(1).add(P3.z.mul(nz.sub(0.5)).mul(0.5)));
      const inside: N = float(1).sub(smoothstep(EDGE_E0, 1, rn)).mul(slabW).mul(facing);
      const coreRaw: N = float(1).sub(smoothstep(coreFrac.sub(0.05), coreFrac.add(0.03), rn));
      const bandRaw: N = smoothstep(coreFrac.sub(0.03), coreFrac.add(0.02), rn)
        .mul(float(1).sub(smoothstep(coreFrac.add(temperW).sub(0.03), coreFrac.add(temperW).add(0.02), rn)))
        .mul(step(1e-4, temperW));
      const temperT: N = saturate(rn.sub(coreFrac).div(max(temperW, 1e-4)));
      const rimT: N = saturate(rn.sub(coreFrac).sub(temperW).div(max(float(1).sub(coreFrac).sub(temperW), 1e-4)));
      const rim: N = pow(float(1).sub(rimT), 1.5).mul(float(1).sub(coreRaw)).mul(float(1).sub(bandRaw)).mul(inside);
      const band: N = bandRaw.mul(inside);
      const core: N = coreRaw.mul(inside);
      // zones in order: the scorched rim, the heat-tint band (bare metal, oxide colours), the core
      over(rim, vec3(P2.y, P2.y, P2.y), vec3(0, 0, 0), 0, P2.z, 1, 0, float(0));
      over(band, vec3(0, 0, 0), temperColor(temperT), 0, 0.36, 0, 1, band);
      over(core, vec3(P0.w, P0.w, P0.w), P0.xyz, P1.x, P1.y, P1.z, P1.w, core);
      S.any.assign(max(S.any, inside));
      if (full) {
        const h: N = c.y;
        S.heat.addAssign(heatColor(h).mul(h.mul(h)).mul(float(1).sub(smoothstep(0, coreFrac.mul(0.9), rn))).mul(inside));
        // THE CRATER: a bowl + a raised lip, scale-free slope H'(ρc) (damage_state.craterSlopeCpu); the normal
        // tilts by −H'·r̂ in the hit's tangent plane — Mikkelsen's surface gradient, added to the material normal
        const rc: N = rn.div(max(P3.w, 1e-4));
        const bowl: N = P3.x.mul(4).mul(rc).mul(float(1).sub(rc.mul(rc))).mul(step(rc, 1));
        const u: N = rc.sub(1).div(0.18);
        const lip: N = P3.y.mul(u.mul(-2 / 0.18)).mul(exp(u.mul(u).negate()));
        const dir: N = dt.div(max(dtl, 1e-5));
        S.grad.addAssign(dir.mul(bowl.add(lip).negate()).mul(inside));
      }
    });
  });
}

// ─── the layer ─────────────────────────────────────────────────────────────

export const damageLayerStats = { marks: 0, full: 0, ground: 0, skipped: 0 };

/**
 * Compose the damage layer onto one material ('marks': albedo / roughness / metalness; 'full': + the crater normal
 * and the heat). Idempotent per material unless `force` (the ground rebuilds its graph on the same material).
 * The caller keys a CLASSIC material's graph (`keyClassicNodeGraph` — THE CLASSIC-KEY LAW); a keyed ground graph
 * (`keyGroundGraph`) is already unique per build. Returns false when nothing was applied.
 */
export function applyDamageLayer(material: THREE.Material, opts: { mode: DamageMode; force?: boolean; ground?: boolean }): boolean {
  if (opts.mode === 'off') return false;
  const m = material as unknown as Record<string, unknown> & { userData: Record<string, unknown>; transparent?: boolean; alphaTest?: number; isMeshStandardMaterial?: boolean; isMeshStandardNodeMaterial?: boolean; needsUpdate: boolean };
  if (!(m.isMeshStandardMaterial || m.isMeshStandardNodeMaterial)) { damageLayerStats.skipped++; return false; }
  if (m.userData.damageLayer && !opts.force) return false;
  if (m.transparent === true || (m.alphaTest ?? 0) > 0) { damageLayerStats.skipped++; return false; } // glass panes / cards
  const full = opts.mode === 'full';
  // The accumulators are created HERE (no TSL stack is current: `.toVar()` only makes the var) and PLACED at the top
  // of the colour slot with `Stack` — created lazily they would be declared where first built: inside the loop's If.
  const S: Slots = {
    aK: vec3(1, 1, 1).toVar(), aC: vec3(0, 0, 0).toVar(), rK: float(1).toVar(), rC: float(0).toVar(),
    mK: float(1).toVar(), mC: float(0).toVar(), heat: vec3(0, 0, 0).toVar(), grad: vec3(0, 0, 0).toVar(), any: float(0).toVar(),
  };
  const prevColor = (m.colorNode as N) ?? null;
  const prevRough = (m.roughnessNode as N) ?? null;
  const prevMetal = (m.metalnessNode as N) ?? null;
  const prevEmissive = (m.emissiveNode as N) ?? null;
  const prevNormal = (m.normalNode as N) ?? null;
  m.colorNode = Fn(() => {
    for (const v of Object.values(S)) Stack(v); // declared + initialised at the top of the fragment (THE LAZY-VAR LAW)
    evaluate(S, full); // FIRST (THE ORDER LAW above)
    const base: N = vec4(prevColor ?? materialColor).toVar();
    return vec4(base.rgb.mul(S.aK).add(S.aC), base.a);
  })();
  m.roughnessNode = Fn(() => float(prevRough ?? materialRoughness).mul(S.rK).add(S.rC))();
  m.metalnessNode = Fn(() => float(prevMetal ?? materialMetalness).mul(S.mK).add(S.mC))();
  if (full) {
    m.emissiveNode = Fn(() => vec3(prevEmissive ?? materialEmissive).add(S.heat.mul(uDamageHeat).mul(uDamageFull)))();
    m.normalNode = Fn(() => {
      // THE LAZY-SAMPLE LAW: the base normal (a normal-map sample) is a var at the top — the select below reads it in both branches
      const base: N = vec3(prevNormal ?? materialNormal).toVar();
      const gL: N = length(S.grad);
      const gw: N = transpose(uFrame).mul(vec4(S.grad, 0)).xyz;       // frame → world (Fᵀ carries a normal-like vector)
      const gW: N = gw.mul(gL.div(max(length(gw), 1e-6)));
      const gV: N = cameraViewMatrix.mul(vec4(gW, 0)).xyz;
      return select(S.any.greaterThan(1e-4), normalize(base.add(gV.mul(uDamageFull))), base);
    })();
  }
  m.userData.damageLayer = opts.mode;
  m.needsUpdate = true;
  if (opts.ground) damageLayerStats.ground++;
  if (full) damageLayerStats.full++; else damageLayerStats.marks++;
  return true;
}
